import React, { useEffect, useState } from 'react';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import {makeStyles} from 'tss-react/mui';

import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import ShareModal from './ShareModal/ShareModal';
import EnTetePage from '../../Layout/EnTetePage/EnTetePage';
import AddBookModal from './AddBookModal/AddBookModal';
import Collection from '../../Collection/Collection';
import Search from '../../Search/Search';

import DetailsBookModal from './DetailsBookModal/DetailsBookModal';
import LendBookModal from './LendBookModal/LendBookModal';
import { getBooks, getCollections } from '../../../slices/itemsSlice';
import { setLoading } from '../../../slices/feedbackSlice';

const useStyles = makeStyles()((theme) => ({
  paper: {
    // 2 et non 8 : `MainLayout` compense maintenant la barre fixe et ajoute
    // l'air nécessaire. Les huit unités d'ici s'y ajoutaient, et le titre
    // commençait au quart de la hauteur de l'écran.
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  image: {
    height: '450px',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  cardContent: {
    padding: '0px',
    '&:last-child': {
      paddingBottom: 0,
    },
    height: '100%',
    alignContent: 'bottom',
  },
  card: {
    height: '100%',
    width: '281.25px',
  },
  bookDetails: {
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    width: '281.25px',
  },
}));


export default function Books() {
  const {classes} = useStyles();
  const [addBookModalOpen, setAddBookModalOpen] = useState(false);
  const [detailsBookModalOpen, setDetailsBookModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState({});
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    query: "",
    available: false,
    archived: false,
    categoryIds : []
  });
  const [lendBookModalOpen, setLendBookModalOpen] = useState(false);
  const [bookToLend, setBookToLend] = useState({});
  const dispatch = useDispatch();
  const { t } = useTranslation('myBooks');
  const collections = useSelector((state) => state.items.collections);
  const categories = useSelector((state) => state.items.categories);
  const books = useSelector((state) => state.items.books);

  useEffect(() => {
    dispatch(getCollections());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getBooks())
      .unwrap()
      .then(() => dispatch(setLoading(false)))
      .catch((err) => {
        console.log(err);
        dispatch(setLoading(false));
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getBooks(filters))
        .unwrap()
        .then(() => dispatch(setLoading(false)))
        .catch((err) => {
          console.log(err);
          dispatch(setLoading(false));
        });
  }, [filters, dispatch]);

  const handleAddBookModalClose = () => {
    setAddBookModalOpen(false);
  };

  const handleAddBookModalOpen = () => {
    setSelectedBook({});
    setAddBookModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
  };

  const handleShareModalOpen = () => {
    setShareModalOpen(true);
  };

  const handleBookOpen = (event, book) => {
    setSelectedBook(book);
    setDetailsBookModalOpen(true);
  };

  const handleBookEdit = (event, book) => {
    setSelectedBook(book);
    setAddBookModalOpen(true);
    setDetailsBookModalOpen(false);
  };

  const handleSearch = (query) => {
    const newFilters = {...filters, query, page:1}
    setFilters(newFilters)
  };

  const handleOnFilterCategories = (event, categoriesQuery) => {
    const categoryIds = categoriesQuery.map(category => category.id)
    const newFilters = {...filters, categoryIds, page:1}
    setFilters(newFilters)
  };

  const handleAvailableFilter = (checked) => {
    const newFilters = {...filters, available: checked, page:1}
    setFilters(newFilters)
  };

  // Les livres archivés sont masqués par défaut. Ce commutateur est le seul
  // moyen de les revoir, donc de les désarchiver : sans lui, archiver serait
  // sans retour depuis l'interface.
  const handleArchivedFilter = (checked) => {
    const newFilters = {...filters, archived: checked, page:1}
    setFilters(newFilters)
  };

  const handleCloseBookModal = () => {
    setDetailsBookModalOpen(false);
    setSelectedBook({});
  };

  const handleBookLend = (event, book) => {
    setDetailsBookModalOpen(false);
    setLendBookModalOpen(true);
    setBookToLend(book);
  };

  const handleLendBookModalClose = () => {
    setLendBookModalOpen(false);
  };

  const handleSetNewPageNumber = (number) => {
    const newFilters = {...filters, page:number}
      setFilters(newFilters)
  }

  return (
    <>
      {shareModalOpen && <ShareModal
        display={shareModalOpen}
        handleClose={handleShareModalClose}
        sharingLink={`https://app.labibli.com/collection/${collections[0]?.slug}`}
      />}
      {addBookModalOpen && (
      <AddBookModal
        display={addBookModalOpen}
        handleClose={handleAddBookModalClose}
        book={selectedBook}
        categories={categories}
      />
      )}
      {lendBookModalOpen && <LendBookModal
        display={lendBookModalOpen}
        handleClose={handleLendBookModalClose}
        book={bookToLend}
      />}
      {detailsBookModalOpen && (
      <DetailsBookModal
        display={detailsBookModalOpen}
        handleClose={handleCloseBookModal}
        handleEdit={handleBookEdit}
        handleLend={handleBookLend}
        book={selectedBook}
        categories={categories}
        isAdmin
        collection={{}}
      />
      ) }

      <Container component="main" className={classes.paper}>
        <Grid container spacing={4}>
          {/* ⚠️ Sans `classes.content`, qui porte un `justifyContent: center`.
              Il est sans effet sur la recherche, qui prend toute la largeur ;
              mais l'en-tête, lui, ne fait que la largeur de son texte, et se
              retrouvait décalé de 400 px du reste de la page. */}
          <Grid item xs={12}>
            <EnTetePage titre={t('page_title')} intro={t('intro')} />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            className={classes.content}
          >
            <Search
              onSearch={handleSearch}
              setOnlyAvailableFilter={handleAvailableFilter}
              setShowArchivedFilter={handleArchivedFilter}
              handleOnFilterCategories={handleOnFilterCategories}
              categories={categories}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className={classes.content}
              >
                <Button
                  variant="contained"
                  component="label"
                  color="secondary"
                  onClick={handleShareModalOpen}
                  className={classes.button}
                >
                  {t('share_button')}
                </Button>

              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className={classes.content}
              >
                <Button
                  variant="contained"
                  component="label"
                  color="secondary"
                  onClick={handleAddBookModalOpen}
                  className={classes.button}
                >
                  {t('add_new_book')}
                </Button>
              </Grid>

              {/* ⛔ L'export est parti dans « Mon compte ». C'est un geste de
                  compte, qu'on fait une fois par an ou le jour où l'on part ;
                  ici il avait le même poids qu'« Ajouter un livre », utilisé
                  tous les jours. */}
            </Grid>
          </Grid>

          <Collection
            books={books}
            onBookClick={handleBookOpen}
            handleSetNewPageNumber={handleSetNewPageNumber}
            page={filters.page}
          />

        </Grid>
      </Container>
    </>
  );
}
